'use client'

import { useId, useState, type CSSProperties } from 'react'
import type { BirdieCardView } from 'src/components/BirdieRoundCard/birdieCardModel'
import { useCountUp } from 'src/hooks/useCountUp'
import type { AvatarRingView } from './avatarRing'
import {
  golferInitials,
  pillFills,
  CAPSULE_BAR_TOTAL,
  CAPSULE_COUNT_UP_MS,
  CAPSULE_PILL_GREEN_DELAY_MS,
  CAPSULE_PILL_STEP_MS,
  CAPSULE_STAT_COUNT_UP_MS,
  type CapsuleStateView
} from './capsuleState'
import { BirdieBallDefs, GolfBall } from './GolfBall'
import { thoughtTypeSteps } from './golferThought'
import type { BirdieRingsView } from './livePutts'
import { RING_STAGGER_MS } from './settlement'
import type { PuttMark } from './tileViewModel'

type Props = Readonly<{
  view: BirdieCardView
  /**
   * The score row. Two sources, one shape: the LIVE per-putt events while the
   * clip plays, then the settled pattern (which overwrites them) once the
   * reveal delay has elapsed. The host picks; the card just draws.
   */
  rings?: BirdieRingsView
  /** Which shape, which box, and what may animate (`capsuleState`). */
  capsule: CapsuleStateView
  /** The avatar countdown (`avatarRing`) — betting, closing, next, or nothing. */
  ring: AvatarRingView
  /**
   * B40: the golfer's thought, when he is having one — the host draws it from
   * the same `livePutts` this card's ring row is drawn from, so it lands on the
   * frame the ball does (`useGolferThought`). Absent most rounds, by design.
   */
  thought?: { text: string; moment: string }
  /**
   * B40: the round's revealed type, when it is one the board draws. It lights the
   * golfer's bezel — cold under frost, warm under fire — and nothing else. Both
   * layouts take it: the desktop capsule's MOTION is desktop's, but a bezel
   * colour is a state, and a 44-px strip can carry a state.
   */
  roundType?: 'frost' | 'fire'
  visible: boolean
  style?: CSSProperties
}>

/**
 * A Birdie round is exactly three putts, so the score row always has three
 * slots. Constant strings (not the map index) so `react/no-array-index-key`
 * stays quiet and a slot keeps its DOM node when its mark arrives.
 */
const PUTT_SLOTS = ['putt-1', 'putt-2', 'putt-3'] as const

/** The avatar's countdown ring, in its own box around the disc. */
const RING_BOX = 48
const RING_RADIUS = 22.5
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

const round2 = (n: number): number => Math.round(n * 100) / 100

// ── Icons ─────────────────────────────────────────────────────────────
// Inline, stroked, `currentColor`, and ALWAYS `aria-hidden`: the row they sit
// in carries the words (`role="img"` + an `aria-label` built from
// `birdieCopy.ts`), so an icon that announced itself would say everything
// twice. Owner, 2026-09-08: "as few labels as possible … icons and other
// visuals" — every one of these replaces a word that used to be on the capsule.
//
// Drawn on a 24-unit grid so one class can size all of them; the stroke width
// is a viewBox unit, which is what keeps the weight identical after the board's
// UI scale (`k`) has been applied.

type IconProps = Readonly<{ className: string }>

const iconBase = { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' } as const

/** The golfer's age. */
function AgeIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} {...iconBase}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  )
}

/** The putt length — a ruler laid down the line. */
function DistanceIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} {...iconBase}>
      <path d="M3 15l12-12 6 6-12 12z" />
      <path d="M8 10l2 2M11 7l2 2M14 4l2 2" />
    </svg>
  )
}

/** `Level` — a club through the ball. */
function ClubIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} {...iconBase}>
      <path d="M19 3L8.5 15.5" />
      <path d="M8.5 15.5c-1.6 1.6-4.6 2.6-6.2 1.5 1.1-2.4 3.2-4 5.4-3.9" />
      <circle cx="20" cy="20" fill="currentColor" r="1.4" />
    </svg>
  )
}

/** `Green difficulty` — the pin. */
function FlagIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} {...iconBase}>
      <path d="M6 21V4" />
      <path d="M6 4h11l-3 4 3 4H6" />
      <path d="M2 21h8" />
    </svg>
  )
}

// ── The golf ball ─────────────────────────────────────────────────────
// B20 lifted it out of this file: the capsule, every tile and count bar on the
// grid, the past-rounds rows and the round sheet all draw the SAME ball now
// (`GolfBall.tsx`), because before B20 the capsule drew a real golf ball and the
// tiles drew an abstract disc-and-ring pair — the same three facts in two visual
// languages on one screen.
//
// The gradients moved with it, from a `useId` pair to two FIXED shared ids, so a
// document may carry several trees that draw balls (this capsule, the overlay,
// the portalled round sheet) and each may declare the defs without knowing
// whether another already has. See `GolfBall.tsx` for why that is safe.

/**
 * The make-chance scene: a ball rolls along a dotted line and drops into the
 * cup, over and over while betting; after the round starts it rests at the lip.
 *
 * Owner, signed off 2026-09-08: "the rolling-ball scene (felt pad, dotted line,
 * cup with rim, ball rolling with a moving shadow, dropping with scale/opacity,
 * a gold cup pulse)". It is the icon for a sentence — "make the putt" — that
 * the capsule no longer has room to write, which is exactly the trade the
 * owner asked for ("as few labels as possible … icons and other visuals").
 *
 * `aria-hidden` in full: the percentage beside it is the readable half, and a
 * looping animation is not something to narrate. The SVG is `overflow: visible`
 * (stylesheet) because the cup's gold pulse expands past the viewBox — clipping
 * it turns the celebration into a rectangle.
 */
function PuttScene({ feltId, cupId }: Readonly<{ feltId: string; cupId: string }>) {
  return (
    <span aria-hidden="true" className="birdie-card__scene">
      <svg className="birdie-card__scene-svg" viewBox="0 0 56 30">
        <defs>
          <radialGradient cx="50%" cy="60%" id={feltId} r="60%">
            <stop offset="0" stopColor="#2f6b43" stopOpacity="0.55" />
            <stop offset="1" stopColor="#2f6b43" stopOpacity="0" />
          </radialGradient>
          <radialGradient cx="50%" cy="35%" id={cupId} r="70%">
            <stop offset="0" stopColor="#16261c" />
            <stop offset="1" stopColor="#060d09" />
          </radialGradient>
        </defs>
        <ellipse cx="24" cy="20" fill={`url(#${feltId})`} rx="23" ry="9" />
        <path
          className="birdie-card__scene-line"
          d="M7 20 Q 19 20.4 29 19.6"
          fill="none"
          strokeDasharray="1.6 2.6"
        />
        <ellipse cx="36" cy="20" fill={`url(#${cupId})`} rx="8.5" ry="4.2" />
        <ellipse
          className="birdie-card__scene-rim"
          cx="36"
          cy="19.3"
          fill="none"
          rx="8.5"
          ry="4.2"
        />
        <ellipse
          className="birdie-card__scene-pulse"
          cx="36"
          cy="19.3"
          fill="none"
          rx="8.5"
          ry="4.2"
        />
      </svg>
      <span className="birdie-card__scene-shadow" />
      <span className="birdie-card__scene-ball">
        <GolfBall className="birdie-card__ball" />
      </span>
    </span>
  )
}

type ScoreProps = Readonly<{
  marks: readonly PuttMark[]
  delaysMs: readonly number[]
  label?: string
  animate: boolean
  /** B34: the pending third slot pulses while the third putt is under way. */
  hot?: boolean
}>

/**
 * The capsule's own live score: three slots, each a cup that a real golf ball
 * drops into when the putt goes in.
 *
 * Owner: "make the state of how many has gone in / missed more prominent as
 * it's an amazing feature" — and, signed off on the mockup, "holed = real golf
 * ball dropping into a green-ringed slot with squash-and-settle and a gold
 * flash, missed = faded ball behind a red ring, pending = dashed ring".
 *
 * Every slot renders its ball from the first frame; the STATE class is what
 * reveals it, which is what lets the drop be a CSS animation that starts on the
 * class change and never replays for a slot that was already filled (the host
 * passes 0 for those). A slot keeps its DOM node for the whole round.
 *
 * Announced as ONE image with the row's label (built by the host from the
 * translated result pattern) rather than three: a screen reader that read
 * "holed, missed, holed" as three unlabelled images would be reading the
 * picture, not the result.
 */
function PuttSlots({ marks, delaysMs, label, animate, hot = false }: ScoreProps) {
  const a11y = label ? { role: 'img' as const, 'aria-label': label } : { 'aria-hidden': true }
  return (
    <div className={`birdie-card__pips${animate ? '' : ' birdie-card__pips--instant'}`} {...a11y}>
      {PUTT_SLOTS.map((slot, index) => {
        const mark = marks[index]
        const delay = animate ? (delaysMs[index] ?? index * RING_STAGGER_MS) : 0
        return (
          <span
            className={`birdie-card__pip birdie-card__pip--${mark ?? 'pending'}${
              hot && mark === undefined && index === PUTT_SLOTS.length - 1
                ? ' birdie-card__pip--hot'
                : ''
            }`}
            key={slot}
            // A custom property, not `animation-delay`: the slot's ring flash
            // and the ball's drop are two animations on two elements, and they
            // have to share one delay or a catch-up burst would flash a ring
            // over a ball that has not arrived yet.
            style={{ '--birdie-pip-delay': `${delay}ms` } as CSSProperties}
          >
            <GolfBall className="birdie-card__ball" />
          </span>
        )
      })}
    </div>
  )
}

/**
 * One stat row of the betting capsule: icon, five pill segments, `n/5`.
 *
 * Signed off 2026-09-08: "the stat pills are a static grey track and ONLY the
 * coloured fill grows left→right across the five segments (continuous, 0.2 s
 * per segment, green a beat after level)". So the track is painted by the
 * stylesheet and never animates; each segment's FILL carries an inline width
 * (its final, true length) plus an inline delay, and the growth is a keyframe
 * with an implicit `to` — `from { width: 0 }` and nothing else, so the day
 * `prefers-reduced-motion` drops the animation the segment is already at its
 * real width rather than at zero.
 *
 * The whole row is one `role="img"`: the label and the number together are what
 * it says, and five separately-announced segments would be noise.
 */
function StatRow({
  icon,
  tone,
  score,
  text,
  label,
  delayMs
}: Readonly<{
  icon: 'club' | 'flag'
  tone: 'level' | 'green'
  score: number
  /** The number as the round's locale writes it, one decimal. */
  text: string
  /** The row's own name, from `birdieCopy.ts`. */
  label: string
  /** The whole row's head start, ms — the green row follows the level row. */
  delayMs: number
}>) {
  const Icon = icon === 'club' ? ClubIcon : FlagIcon
  return (
    <div
      aria-label={`${label} ${text}/${CAPSULE_BAR_TOTAL}`}
      className={`birdie-card__stat-row birdie-card__stat-row--${tone}`}
      role="img"
    >
      <Icon className="birdie-card__stat-icon" />
      <span aria-hidden="true" className="birdie-card__pills">
        {pillFills(score).map((fill, index) => (
          <span
            className="birdie-card__pill"
            // eslint-disable-next-line react/no-array-index-key -- five fixed segments; the index IS the identity
            key={index}
            style={
              {
                '--birdie-pill-w': `${round2(fill * 100)}%`,
                '--birdie-pill-delay': `${delayMs + index * CAPSULE_PILL_STEP_MS}ms`
              } as CSSProperties
            }
          >
            <span className="birdie-card__pill-fill" />
          </span>
        ))}
      </span>
      <span aria-hidden="true" className="birdie-card__num">
        {text}
        <em>/{CAPSULE_BAR_TOTAL}</em>
      </span>
    </div>
  )
}

/**
 * The two stat rows, mounted only while betting is open.
 *
 * Their own component so the count-ups and the pill fills REPLAY on every
 * `race_ready` without a key: the block does not exist during the clip, so
 * returning to `expanded` is a fresh mount, `useCountUp` starts at zero again
 * and each segment's animation runs from the top. Two rounds with the same
 * level therefore still animate, which a `key` derived from the values could
 * not manage.
 */
function StatRows({ view }: Readonly<{ view: BirdieCardView }>) {
  const level = useCountUp(view.levelScore, CAPSULE_STAT_COUNT_UP_MS)
  const green = useCountUp(view.greenScore, CAPSULE_STAT_COUNT_UP_MS)
  return (
    <div className="birdie-card__stats">
      <StatRow
        delayMs={0}
        icon="club"
        label={view.levelLabel}
        score={view.levelScore}
        text={view.scoreAt(level)}
        tone="level"
      />
      <StatRow
        delayMs={CAPSULE_PILL_GREEN_DELAY_MS}
        icon="flag"
        label={view.difficultyLabel}
        score={view.greenScore}
        text={view.scoreAt(green)}
        tone="green"
      />
    </div>
  )
}

/**
 * The make chance, the one number every price on the board is derived from.
 *
 * Deliberately NOT keyed on the capsule's layout: it is on screen in every
 * state, and remounting it at `race_started` would restart the count-up from
 * zero at the exact moment the capsule is sliding across the frame. `useCountUp`
 * animates from whatever is displayed, so the first round counts 0 → 70 and a
 * later round eases 70 → 55; the only case it cannot dramatise is two
 * consecutive rounds priced identically, where there is nothing to count.
 */
function MakeChance({ view, expanded }: Readonly<{ view: BirdieCardView; expanded: boolean }>) {
  const percent = useCountUp(view.makeRatePercent, CAPSULE_COUNT_UP_MS)
  return (
    <div className="birdie-card__mc">
      <span className="birdie-card__rate">{view.makeRateAt(percent)}</span>
      {expanded ? <span className="birdie-card__rate-caption">{view.makeRateCaption}</span> : null}
    </div>
  )
}

/**
 * The avatar's countdown ring (B16), now the ONLY countdown on the board.
 *
 * Owner, 2026-09-08: "remove the countdown number itself, the circle around the
 * avatar seems enough". So `avatarRing`'s `seconds` output is deliberately not
 * rendered anywhere — the ring drains gold over the betting window, turns amber
 * as it closes, and fills white toward the next round.
 *
 * `stroke-dashoffset` is written straight from the prop and TRANSITIONED over
 * the host's 1 Hz tick by the stylesheet — the same technique (and the same
 * reason) as the arc hairline: B11 item 4 proved that easing a stepped value in
 * a `requestAnimationFrame` loop stalls for ~0.4 s of every second and freezes
 * outright in a backgrounded tab, while the compositor interpolates a CSS
 * transition whether or not this tab has frames to spare.
 */
function AvatarCountdown({ ring }: Readonly<{ ring: AvatarRingView }>) {
  return (
    <svg
      aria-hidden="true"
      className={`birdie-card__timer birdie-card__timer--${ring.mode}`}
      height={RING_BOX}
      viewBox={`0 0 ${RING_BOX} ${RING_BOX}`}
      width={RING_BOX}
    >
      <circle
        className="birdie-card__timer-track"
        cx={RING_BOX / 2}
        cy={RING_BOX / 2}
        fill="none"
        r={RING_RADIUS}
      />
      <circle
        className="birdie-card__timer-arc"
        cx={RING_BOX / 2}
        cy={RING_BOX / 2}
        fill="none"
        r={RING_RADIUS}
        style={{
          strokeDasharray: round2(RING_CIRCUMFERENCE),
          strokeDashoffset: round2((1 - ring.drain) * RING_CIRCUMFERENCE)
        }}
      />
    </svg>
  )
}

/**
 * The golfer capsule — one glass strip that MOVES and reshapes (B16b, built to
 * the signed-off mockup `scratchpad/birdie-capsule-final.tpl.html`).
 *
 * Owner, 2026-09-08: "the capsule should maybe be at the center top of the
 * screen during the betting phase, and when betting is over it moves to the top
 * right like now … remove the countdown number itself … the name of the golfer,
 * and also his age … below the name, and then also the length … as few labels
 * as possible … icons and other visuals … and then the current state of how
 * many gone in or missed … maybe not that 2 of 3 in, not needed, the visuals
 * should be enough".
 *
 *  - **betting** (`expanded`, centre-top, 384 x 201 reference px): head row
 *    (avatar with the draining ring, name, and a figures line of icons — age,
 *    distance, the handicap chip), then `Level` and `Green difficulty` as five
 *    coloured pill segments with their `n/5`, then the make-chance row: the
 *    rolling-ball scene, the big percentage, its one caption and the tempo tag.
 *  - **the clip** (`condensed`, top-right, 345.6 x 130): the same head row
 *    without the handicap chip, and the make-chance row with the scene at rest,
 *    a smaller percentage and the three live score slots.
 *  - **settled** (`final`): the same strip, a gold edge and one gold sweep. No
 *    headline, no numeral — "the visuals should be enough".
 *
 * Nothing in here is sized by its contents: the host pins the box
 * (`cardBoxPx(layout)`, which returns the same two boxes `capsulePosition`
 * names) and every glyph is a fixed reference px, so the only thing that
 * changes between states is which children exist. That is also the belt B11
 * forged against the stream module's `img` rule, which once took the plaque to
 * 49 % of the frame.
 *
 * The headshot falls back to the golfer's initials on the neutral disc both
 * when the round carries no URL and when the CDN image fails to load — tracking
 * the failed URL rather than a bare boolean so the next round's headshot is
 * retried without an effect.
 */
export function BirdieCard({
  view,
  rings,
  capsule,
  ring,
  roundType,
  thought,
  visible,
  style
}: Props) {
  const [failedShotUrl, setFailedShotUrl] = useState<string | undefined>(undefined)
  const shotUrl =
    view.headshotUrl !== undefined && view.headshotUrl !== failedShotUrl
      ? view.headshotUrl
      : undefined

  // One id per capsule instance, four gradients hung off it. Two capsules in a
  // document (the overlay and the mobile strip, or a round sheet beside either)
  // must not share the gradient ids that DIFFER between capsules. Since B20
  // the ball's two are deliberately shared and fixed (`GolfBall.tsx`); what is
  // left per-capsule is the scene's felt and cup, which is what `uid` is for.
  //
  // `useId` returns a value wrapped in COLONS (":r7:"), deliberately, so it
  // cannot collide with an author id. A colon is legal in an id attribute and in
  // the URL fragment an SVG paint reference is — but it is not legal in a CSS
  // selector, so the day anything reaches for one of these with
  // `querySelector` it would throw rather than miss. Substituting rather than
  // stripping keeps the mapping one-to-one, so two capsules cannot land on the
  // same id.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '-')

  const { layout, position } = capsule
  const expanded = layout === 'expanded'

  /**
   * B40 — THE GOLFER REACTS, AND THE IMAGE IS NEVER TOUCHED.
   *
   * The avatar is any square .png a round happens to carry, so nothing here may
   * assume anything about it: what reacts is the FRAME. A holed putt pushes the
   * ring in and flares it, a miss pulls it back and cools it, and the picture
   * inside is the same picture either way.
   *
   * The class carries the putt's NUMBER as well as its result, and that is the
   * whole mechanism: a capsule is mounted for the length of a round, so a class
   * that only said "holed" would not change between two holed putts and a CSS
   * one-shot would fire once and never again. `h1`, `h2`, `h3` are three
   * different rules running one pair of keyframes.
   *
   * Gated on `animateScore`, which is false at settlement and under reduced
   * motion — so the reaction belongs to the live clip and the settled card is
   * still.
   */
  const animate = capsule.animateScore
  const marks = rings?.marks ?? []
  const lastMark = marks.length === 0 ? undefined : marks[marks.length - 1]
  const reaction =
    animate && lastMark !== undefined
      ? `birdie-card--react-${lastMark === 'holed' ? 'h' : 'm'}${String(marks.length)}`
      : ''

  const classes = [
    'birdie-card',
    `birdie-card--${layout}`,
    `birdie-card--${position}`,
    // B40: the round's own weather reaches the golfer. A bezel lit cold under
    // frost and warm under fire is the cheapest possible way to say the round
    // he is playing is not an ordinary one, and it costs no art.
    roundType === undefined ? '' : `birdie-card--${roundType}`,
    // B40: the last three seconds of a betting window. The beat is the SECOND,
    // so the class changes once a second and the tick replays on a capsule that
    // is mounted for the whole round — the same mechanism as the reaction above.
    ring.urgentBeat === undefined ? '' : `birdie-card--urgent-${String(ring.urgentBeat)}`,
    reaction,
    visible ? '' : 'birdie-card--hidden',
    capsule.animateSlide ? '' : 'birdie-card--no-slide',
    capsule.animateScene ? 'birdie-card--scene-live' : '',
    capsule.animateStats ? 'birdie-card--stats-play' : '',
    capsule.sweep ? 'birdie-card--sweep' : ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div aria-label={view.title} className={classes} role="group" style={style}>
      <BirdieBallDefs />

      <div className="birdie-card__head">
        <div className="birdie-card__avatar">
          {/*
            B40 — the thought bubble. Anchored to the avatar and sized so it
            cannot reach a tile, a price or a chip: it hangs ABOVE the capsule,
            which on both layouts is clear of the option grid.

            `key` on the moment, so each one enters rather than the text being
            swapped inside a bubble that is already up — and so a bubble that
            holds across the length of its moment does not re-animate.

            `aria-hidden`: it is flavour, and a screen-reader player is already
            being told the score by the slots beside it. A two-word interjection
            announced over a live round would be noise.
          */}
          {thought === undefined ? null : (
            <span
              aria-hidden="true"
              className="birdie-card__thought"
              key={`thought-${thought.moment}`}
              style={
                { '--birdie-type-steps': String(thoughtTypeSteps(thought.text)) } as CSSProperties
              }
            >
              {/*
                The text is laid out ONCE, in full. The bubble OPENS over it —
                one clip on the whole thing, glass and words together — so it
                grows with its line rather than sitting there as an empty box
                being filled in, and the reveal still cannot break a script,
                because the shaping has already happened.
              */}
              <span className="birdie-card__thought-text">{thought.text}</span>
            </span>
          )}
          {shotUrl === undefined ? (
            // Decorative (B11 item 9 / review 21): the golfer's name is the very
            // next node, so announcing the initials would read it twice.
            <span aria-hidden="true" className="birdie-card__shot birdie-card__shot--empty">
              {golferInitials(view.golferName)}
            </span>
          ) : (
            // eslint-disable-next-line @next/next/no-img-element -- CDN headshot, no loader configured
            <img
              alt=""
              className="birdie-card__shot"
              onError={() => {
                setFailedShotUrl(view.headshotUrl)
              }}
              src={shotUrl}
            />
          )}
          {ring.mode === 'none' ? null : <AvatarCountdown ring={ring} />}
        </div>

        <div className="birdie-card__who">
          <span className="birdie-card__name">{view.golferName}</span>
          {/*
            Icons only, in the owner's own order: age, then length, then the
            handicap — which stays a WORD because "HCP" is golf-wide vocabulary
            and no icon says it. The chip is hidden by the stylesheet once the
            round starts; the head row is otherwise identical in both boxes, so
            the slide never re-flows it.
          */}
          <div className="birdie-card__figs">
            <span className="birdie-card__fig">
              <AgeIcon className="birdie-card__fig-icon" />
              <b>{view.ageLabel}</b>
            </span>
            {view.distanceLabel === undefined ? null : (
              <span className="birdie-card__fig">
                <DistanceIcon className="birdie-card__fig-icon" />
                <b>{view.distanceLabel}</b>
              </span>
            )}
            {view.handicapLabel === undefined ? null : (
              <span className="birdie-card__hcp">{view.handicapLabel}</span>
            )}
          </div>
        </div>
      </div>

      {expanded ? <StatRows view={view} /> : null}

      <div className="birdie-card__total">
        <PuttScene cupId={`${uid}-cup`} feltId={`${uid}-felt`} />
        <MakeChance expanded={expanded} view={view} />
        {expanded ? (
          <span className={`birdie-card__tempo birdie-card__tempo--${view.tempo}`}>
            {view.tempoLabel}
          </span>
        ) : (
          // Three slots from the moment the clip starts, even before the first
          // putt: B12 turned the engine's burned-in indicator off, so this row
          // is the whole live score. Not drawn while betting — there is nothing
          // to show yet, and the room it takes is the tempo tag's.
          <PuttSlots
            animate={capsule.animateScore}
            delaysMs={rings?.delaysMs ?? []}
            hot={rings?.hot === true}
            {...(rings?.label === undefined ? {} : { label: rings.label })}
            marks={rings?.marks ?? []}
          />
        )}
      </div>
    </div>
  )
}
